import { LinkText, Paragraph, Text } from '@/components/common/Typography';
import { toDollars } from '@/util/currency';
import { ListItem, UnorderedList } from '@chakra-ui/react';
import { WeddingGoal } from '@common/goals';
import { getGoalBaseConfig } from '@common/react/goals';
import { WeddingExpenseLevel } from '@common/types';
import { dt, isPastDate } from '@common/util/dateTime';
import { getRoundedUp } from '@common/util/math';
import pluralize from '@common/util/pluralize';
import { capitalize } from '@common/util/strings';
import { isEmpty, isNumber } from '@common/util/types';
import isEqual from 'lodash.isequal';
import GoalStatementLinkText from '../GoalStatementLinkText';
import {
    getIOrWe,
    getRecommendedRiskScoreUsingTargetDate,
    getTargetDateFromYearMonth,
    getYearsOldStr,
} from '../helpers';
import { GoalConfig, GoalData } from './types';

const expenseLevelInfoByValue: Record<
    WeddingExpenseLevel,
    {
        label: string;
        costPerGuest: number;
    }
> = {
    BUDGET: { label: 'Budget', costPerGuest: 188_00 }, // $188
    MODERATE: { label: 'Moderate', costPerGuest: 343_00 }, // $343
    GENEROUS: { label: 'Generous', costPerGuest: 563_00 }, // $563
    LUXURY: { label: 'Luxury', costPerGuest: 1_070_00 }, // $1,070
};

export const WEDDING: GoalConfig<WeddingGoal> = {
    inputSteps: [
        {
            id: 'guests',
            title: 'Roughly, how many guests will you invite?',
            fieldConfigs: [
                {
                    fieldName: 'guests',
                    fieldType: 'integer',
                    fieldValueDefault: 105,
                    min: 1,
                    slider: {
                        min: 1,
                        max: 500,
                        step: 1,
                    },
                    bottomContent: ({ goalData }) => {
                        const { guests } = goalData;
                        if (!isNumber(guests)) return <></>;
                        return (
                            <>
                                {/* HACK: This will be replaced as part of PLE-327 */}
                                <Text
                                    fontSize="xl"
                                    ml={guests < 10 ? 5 : guests < 100 ? 8 : 11}
                                    mt={'-37px'}
                                >
                                    {pluralize('guests', guests, false)}
                                </Text>
                                <Paragraph
                                    fontSize="sm"
                                    color="textMuted"
                                    mt={8}
                                >
                                    {`In 2022, weddings had an average of 105 guests. Courtesy of The Knot.`}
                                </Paragraph>
                            </>
                        );
                    },
                },
            ],
        },
        {
            id: 'expenseLevel',
            title: `What’s your wedding spending style?`,
            fieldConfigs: [
                {
                    fieldName: 'expenseLevel',
                    fieldType: 'select',
                    fieldOptions: Object.entries(expenseLevelInfoByValue).map(
                        ([value, info]) => ({ value, label: info.label })
                    ),
                    fieldValueDefault: 'MODERATE',
                    bottomContent: (
                        <Paragraph fontSize="sm" color="textMuted" mt={4}>
                            {`72% of couples receive some financial support for wedding expenses. Source: WeddingWire`}
                        </Paragraph>
                    ),
                },
            ],
        },
        {
            id: 'yearMonth',
            title: 'Ideally, when do you want to have your wedding?',
            fieldConfigs: [
                {
                    fieldName: 'yearMonth',
                    fieldType: 'yearMonth',
                    bottomContent: ({ goalData, userInfo }) => (
                        <Paragraph fontSize="sm" color="textMuted" mt={4} h={5}>
                            {goalData.targetDate
                                ? getYearsOldStr({
                                      date: goalData.targetDate,
                                      userInfo,
                                  })
                                : undefined}
                        </Paragraph>
                    ),
                },
            ],
            isNextButtonDisabled: (goalData) =>
                !goalData.targetDate || isPastDate(goalData.targetDate),
        },
        {
            id: 'targetAmount',
            title: 'Choose a target wedding cost that you feel comfortable with.',
            fieldConfigs: [
                {
                    fieldName: 'targetAmount',
                    fieldType: 'currency',
                    min: 0,
                    slider: ({ goalData }) => {
                        return goalData.projectedAmount
                            ? {
                                  min: 0,
                                  max: goalData.projectedAmount * 2,
                                  step: 1_000_00, // $1,000
                              }
                            : {
                                  min: 0,
                                  max: 100_000_00, // $100,000
                                  step: 1_000_00, // $1,000
                              };
                    },
                    bottomContent: ({ goalData, showSlideup }) =>
                        goalData.expenseLevel &&
                        goalData.guests &&
                        goalData.projectedAmount ? (
                            <>
                                <Paragraph
                                    fontSize="sm"
                                    color="textMuted"
                                    mt={8}
                                >
                                    {`The estimated cost for a ${goalData.expenseLevel.toLowerCase()} wedding hosting ${
                                        goalData.guests
                                    } guests is around ${toDollars(
                                        goalData.projectedAmount
                                    )}.`}
                                </Paragraph>
                                <LinkText
                                    fontSize="sm"
                                    fontWeight="medium"
                                    mt={3}
                                    onClick={() => {
                                        showSlideup('targetAmountAssumptions');
                                    }}
                                >
                                    See assumptions
                                </LinkText>
                            </>
                        ) : (
                            <></>
                        ),
                },
            ],
        },
    ],
    assumptions: [
        {
            id: 'targetAmountAssumptions',
            title: 'Assumptions',
            content: () => (
                <>
                    <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                        Your estimated wedding price is calculated using:
                    </Paragraph>
                    <UnorderedList fontSize="sm" lineHeight="tall" spacing={1}>
                        <ListItem>
                            Zola and the Knot’s national averages for different
                            types of weddings with different numbers of guests
                        </ListItem>
                        <ListItem>
                            An additional analysis of expenses that increase
                            with the number of guests (food) vs. one-time
                            expenses (like your clothing)
                        </ListItem>
                        <ListItem>
                            Items we’ve included: ceremony / reception venue
                            rental, wedding attire, a band or DJ,
                            transportation, flowers, photography / videography,
                            catering, and alcohol.
                        </ListItem>
                    </UnorderedList>
                </>
            ),
        },
    ],
    getGoalStatementContent: ({ goalData, onGoalStatementClick }) => {
        if (isEmpty(goalData.targetAmount) || isEmpty(goalData.targetDate)) {
            console.warn(
                'Failed to construct goal statement due to incomplete goalData.'
            );
            console.warn(goalData);
            return null;
        }

        const guestsStr = goalData.guests
            ? `${goalData.guests} guests`
            : undefined;
        const expenseLevelStr = goalData.expenseLevel
            ? expenseLevelInfoByValue[goalData.expenseLevel].label.toLowerCase()
            : undefined;

        const iOrWe = getIOrWe(goalData);
        const color = WEDDING.color;

        return (
            <>
                {`${capitalize(iOrWe)} want to have `}
                <>
                    <GoalStatementLinkText
                        text={toDollars(goalData.targetAmount)}
                        onClick={() => onGoalStatementClick('targetAmount')}
                        color={color}
                    />{' '}
                </>
                {guestsStr && (
                    <>
                        {'for '}
                        <GoalStatementLinkText
                            text={guestsStr}
                            onClick={() => onGoalStatementClick('guests')}
                            color={color}
                        />{' '}
                    </>
                )}
                {'for a '}
                {expenseLevelStr && (
                    <>
                        <GoalStatementLinkText
                            text={expenseLevelStr}
                            onClick={() => onGoalStatementClick('expenseLevel')}
                            color={color}
                        />{' '}
                    </>
                )}
                {'wedding by '}
                <GoalStatementLinkText
                    text={dt(goalData.targetDate).format('MMMM, YYYY')}
                    onClick={() => onGoalStatementClick('yearMonth')}
                    color={color}
                />
                {'.'}
            </>
        );
    },
    getAssumptionsContent: () => {
        return (
            <>
                <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                    {'Your estimated wedding price is calculated using:'}
                </Paragraph>
                <UnorderedList fontSize="sm" lineHeight="tall" spacing={1}>
                    <ListItem>{`Zola and the Knot’s national averages for different types of weddings with different guests`}</ListItem>
                    <ListItem>{`Items we’ve included: ceremony / reception venue rental, wedding attire, a band or DJ, transportation, flowers, photography / videography, catering, and alcohol.`}</ListItem>
                </UnorderedList>
            </>
        );
    },
    calculateDerivedGoalData: async ({
        oldGoalData,
        newGoalData,
    }): Promise<GoalData<WeddingGoal>> => {
        const targetDate =
            oldGoalData.yearMonth !== newGoalData.yearMonth &&
            newGoalData.yearMonth?.length === 6
                ? getTargetDateFromYearMonth(newGoalData.yearMonth)
                : oldGoalData.targetDate;

        const projectedAmountDecimal =
            newGoalData.guests && newGoalData.expenseLevel
                ? newGoalData.guests *
                  expenseLevelInfoByValue[newGoalData.expenseLevel].costPerGuest
                : undefined;
        const projectedAmount = projectedAmountDecimal
            ? getRoundedUp(projectedAmountDecimal, 500_00) // $500
            : undefined;

        return {
            ...newGoalData,
            targetDate,
            projectedAmount,

            // update targetAmount if projected amount changed using formula above
            ...(!isEqual(oldGoalData.projectedAmount, projectedAmount) && {
                targetAmount: projectedAmount,
            }),
        };
    },
    getRecommendedRiskScore: (config) =>
        getRecommendedRiskScoreUsingTargetDate(config),
    getTargetDate: ({ goalData }) => goalData.targetDate,
    ...getGoalBaseConfig('WEDDING'),
};
